import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { X, Copy, Download, Share2, Check, Link2 } from "lucide-react"
import type { AssessmentSessionResponse } from "~/api/assessmentSessions"
import { createShareCard } from "~/api/shareCards"
import { ShareableCardImage } from "~/components/ShareableCardImage"
import { generateShareImage, downloadShareImage, shareGenerationErrorMessage } from "~/utils/shareImageGenerator"
import { useWebShare } from "~/hooks/useWebShare"
import {
  DYNAMO_DOG_URL,
  shareCardText,
  shareCardTitle,
  shouldPreferUrlShare,
} from "~/utils/shareCardShare"

interface ShareableCardModalProps {
  isOpen: boolean
  onClose: () => void
  data: AssessmentSessionResponse
  personalizedPortraitUrl?: string | null
}

export function ShareableCardModal({ isOpen, onClose, data, personalizedPortraitUrl }: ShareableCardModalProps) {
  const [aspectRatio, setAspectRatio] = useState<"square" | "vertical">("vertical")
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [imageCopied, setImageCopied] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [shareLinkError, setShareLinkError] = useState<string | null>(null)
  const imageRef = useRef<HTMLDivElement>(null)
  const { share: shareNative, isSupported: canNativeShare } = useWebShare()

  const archetypeLabel = data.archetype?.name ?? data.archetype_label ?? "an archetype"
  const title = shareCardTitle(data.dog_name, archetypeLabel)

  const generateImage = useCallback(async () => {
    if (!imageRef.current) return
    setIsGenerating(true)
    setError(null)
    try {
      const result = await generateShareImage(imageRef.current, { quality: 0.95, scale: 2 })
      setImageDataUrl(result.dataUrl)
    } catch (err) {
      setError(shareGenerationErrorMessage(err))
    } finally {
      setIsGenerating(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return
    setImageDataUrl(null)
    const frame = requestAnimationFrame(() => {
      void generateImage()
    })
    return () => cancelAnimationFrame(frame)
  }, [isOpen, aspectRatio, generateImage])

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setShareUrl(null)
    setShareLinkError(null)
    createShareCard(aspectRatio)
      .then((card) => {
        if (!cancelled) setShareUrl(card.share_url)
      })
      .catch((err) => {
        if (!cancelled) {
          setShareLinkError(err instanceof Error ? err.message : "Could not create share link")
        }
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, aspectRatio])

  if (!isOpen) return null

  async function handleCopyImage() {
    if (!imageDataUrl) return
    if (typeof ClipboardItem === "undefined") {
      setError("Copying images is not supported in this browser")
      return
    }

    try {
      const response = await fetch(imageDataUrl)
      const blob = await response.blob()
      const clipboardItem = new ClipboardItem({ [blob.type]: blob })
      await navigator.clipboard.write([clipboardItem])
      setImageCopied(true)
      setTimeout(() => setImageCopied(false), 1800)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not copy image")
    }
  }

  async function handleCopyLink() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 1800)
    } catch {
      setShareLinkError("Could not copy link")
    }
  }

  function handleDownload() {
    if (!imageDataUrl) return
    const slug = archetypeLabel
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
    downloadShareImage(imageDataUrl, `doggy-dynamo-${data.dog_name.toLowerCase()}-${slug}.png`)
  }

  async function handleNativeShare() {
    try {
      if (shouldPreferUrlShare(shareUrl) && shareUrl) {
        await shareNative({
          title,
          text: shareCardText(shareUrl),
          url: shareUrl,
        })
        return
      }

      if (!imageDataUrl) return
      const response = await fetch(imageDataUrl)
      const blob = await response.blob()
      const file = new File([blob], "doggy-dynamo-share-card.png", { type: "image/png" })
      await shareNative({
        title,
        text: shareCardText(shareUrl || DYNAMO_DOG_URL),
        files: [file],
      })
    } catch (err) {
      if (err instanceof Error && err.name !== "AbortError") {
        setError("Could not share")
      }
    }
  }

  const canShare = canNativeShare && Boolean(shareUrl || imageDataUrl)

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative mx-4 flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-[var(--color-surface-card)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--color-divider)] px-6 py-4">
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">Share card</h2>
          <button
            onClick={onClose}
            className="rounded-full border border-[var(--color-divider)] p-2 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-primary)]"
            aria-label="Close share modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div className="mb-4 flex justify-center gap-3">
            <button
              type="button"
              onClick={() => setAspectRatio("vertical")}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                aspectRatio === "vertical"
                  ? "bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                  : "border border-[var(--color-divider)] text-[var(--color-text-primary)]"
              }`}
            >
              Vertical
            </button>
            <button
              type="button"
              onClick={() => setAspectRatio("square")}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                aspectRatio === "square"
                  ? "bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                  : "border border-[var(--color-divider)] text-[var(--color-text-primary)]"
              }`}
            >
              Square
            </button>
          </div>

          <div className="mb-5 flex justify-center">
            <div
              ref={imageRef}
              className="pointer-events-none fixed left-0 top-0 -z-50 opacity-0"
              aria-hidden
            >
              <ShareableCardImage
                data={data}
                aspectRatio={aspectRatio}
                personalizedPortraitUrl={personalizedPortraitUrl}
              />
            </div>
            {isGenerating ? (
              <div className="rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-10 py-12 text-[var(--color-text-secondary)]">
                Generating image...
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-3">
                <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-4 text-center text-red-700">
                  {error}
                </div>
                <button
                  type="button"
                  onClick={() => void generateImage()}
                  disabled={isGenerating}
                  className="rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] disabled:opacity-50"
                >
                  Retry
                </button>
              </div>
            ) : imageDataUrl ? (
              <div className="flex w-full max-w-[360px] justify-center overflow-hidden rounded-xl border border-[var(--color-divider)] bg-white">
                <img
                  src={imageDataUrl}
                  alt="Share card preview"
                  className="block h-auto max-h-[min(70vh,560px)] w-full object-contain"
                />
              </div>
            ) : null}
          </div>

          {shareLinkError && (
            <p className="mb-3 text-center text-sm text-red-700">{shareLinkError}</p>
          )}

          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => void handleCopyLink()}
              disabled={!shareUrl}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
            >
              {linkCopied ? <Check size={16} /> : <Link2 size={16} />}
              {linkCopied ? "Link copied!" : "Copy link"}
            </button>
            {canShare && (
              <button
                type="button"
                onClick={() => void handleNativeShare()}
                disabled={!shareUrl && (!imageDataUrl || isGenerating)}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] disabled:opacity-50"
              >
                <Share2 size={16} />
                Share
              </button>
            )}
            <button
              type="button"
              onClick={() => void handleCopyImage()}
              disabled={!imageDataUrl || isGenerating}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] disabled:opacity-50"
            >
              {imageCopied ? <Check size={16} /> : <Copy size={16} />}
              {imageCopied ? "Copied!" : "Copy image"}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={!imageDataUrl || isGenerating}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] disabled:opacity-50"
            >
              <Download size={16} />
              Download
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
